import deepEqual from "deep-equal";
import { Preference, UpdatedPreference } from "./types";
import { Account } from "../../api/dealer/DealerApi";
import { RBACRole } from "../../api/rbac/types";
import { UserDetails } from "../../context/UserContext/UserContext";

export const updateDelta = (
  prevDelta: Preference[],
  updatedPref: UpdatedPreference
): UpdatedPreference[] => {
  const updatedDelta = prevDelta.reduce((delta, pref) => {
    if (pref.key === updatedPref.key) {
      if (updatedPref.value !== undefined && updatedPref.value !== pref.value)
        return [
          ...delta,
          {
            ...pref,
            value: updatedPref.value,
          },
        ];
      if (
        updatedPref.config !== undefined &&
        updatedPref.config !== pref.config
      ) {
        return [
          ...delta,
          {
            ...pref,
            config: updatedPref.config,
          },
        ];
      }
      if (
        updatedPref.userOverridable !== undefined &&
        updatedPref.userOverridable !== pref.userOverridable
      )
        return [
          ...delta,
          {
            ...pref,
            userOverridable: updatedPref.userOverridable,
          },
        ];
      return delta;
    }
    return [...delta, pref];
  }, [] as Preference[]);
  return updatedDelta.filter((p) => p.key === updatedPref.key).length
    ? updatedDelta
    : [...updatedDelta, updatedPref];
};

export const removeDefaultPreferences = (
  defaultPreferences: Preference[] = [],
  delta: UpdatedPreference[]
): Preference[] =>
  delta.reduce((cleanedDelta, pref) => {
    const defaultPref: Preference = defaultPreferences.find(
      ({ key }) => key === pref.key
    ) as Preference;

    const updatedPrefWithDefaults: Preference = {
      ...pref,
      key: pref.key,
      value: pref.value === undefined ? defaultPref.value : pref.value,
      userOverridable:
        pref.userOverridable === undefined
          ? defaultPref.userOverridable
          : pref.userOverridable,
    };

    if (
      defaultPref.key === pref.key &&
      defaultPref.value === updatedPrefWithDefaults.value &&
      defaultPref.userOverridable === updatedPrefWithDefaults.userOverridable &&
      deepEqual(defaultPref.config, updatedPrefWithDefaults.config)
    ) {
      return cleanedDelta;
    }

    return [...cleanedDelta, updatedPrefWithDefaults];
  }, [] as Preference[]);

export const isAuthorizedForMultipleAccounts = (
  userRole: UserDetails | undefined,
  enableMultipleAccounts: boolean,
  enableRbacAuthSupportForCorporateView: boolean = false
): boolean => {
  if (enableRbacAuthSupportForCorporateView) {
    if (userRole?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      return false;
    }
    return isAuthorizedCorporateUser(
      userRole,
      enableRbacAuthSupportForCorporateView
    );
  }

  if (enableMultipleAccounts) {
    return !(
      userRole &&
      (userRole.is(RBACRole.JD_ADMIN) ||
        userRole.is(RBACRole.JD_DEVELOPER_ADMIN) ||
        userRole.is(RBACRole.JDPS_SERVICE_DEALER))
    );
  }

  return isAuthorizedCorporateUser(userRole, false);
};

export const isAuthorizedCorporateUser = (
  userRole: UserDetails | undefined,
  enableRbacAuthSupportForCorporateView: boolean = false
): boolean => {
  if (!userRole) {
    return false;
  }

  if (enableRbacAuthSupportForCorporateView) {
    return (
      userRole.is(RBACRole.CORPORATE_VIEW) ||
      userRole.is(RBACRole.CORPORATE_PARTS_MANAGER)
    );
  }

  return userRole.is(RBACRole.CORPORATE_PARTS_MANAGER);
};

export const getDealerAccountFromLocation = (
  primaryAccount: Account | undefined
): string =>
  primaryAccount?.location
    ? `${primaryAccount.id} ${primaryAccount.name}, ${primaryAccount.location.city}, ${primaryAccount.location.state} ${primaryAccount.location.country}`
    : primaryAccount?.id || "";
